import { Send, Trash2 } from "lucide-react";
import {
  useCreate,
  useDelete,
  useGetIdentity,
  useGetList,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Create } from "@/components/admin/create";
import { Edit } from "@/components/admin/edit";
import { SimpleForm } from "@/components/admin/simple-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import {
  SaleAvatar,
  SectionCard,
  todayIso,
  useFormatDate,
} from "../common/components";
import type { TeamTask } from "../types";
import { TeamTaskInputs, transformTeamTask } from "./TeamTaskInputs";

export const TeamTaskCreate = () => {
  const { identity } = useGetIdentity();
  return (
    <Create
      resource="team_tasks"
      redirect="list"
      transform={transformTeamTask}
      className="mx-auto w-full max-w-3xl"
    >
      <Card>
        <CardContent>
          <SimpleForm
            className="max-w-none"
            defaultValues={{
              status: "todo",
              priority: "medium",
              assignee_id: identity?.id ?? null,
              checklist: [],
              tags: [],
            }}
          >
            <TeamTaskInputs />
          </SimpleForm>
        </CardContent>
      </Card>
    </Create>
  );
};

export const TeamTaskEdit = () => (
  <Edit
    resource="team_tasks"
    redirect="list"
    mutationMode="pessimistic"
    transform={transformTeamTask}
    className="mx-auto w-full max-w-5xl"
  >
    <div className="grid gap-4 lg:grid-cols-[1fr_320px] [&>*]:min-w-0 [&>*>*]:min-w-0">
      <Card>
        <CardContent>
          <SimpleForm className="max-w-none">
            <TeamTaskInputs />
          </SimpleForm>
        </CardContent>
      </Card>
      <div className="flex flex-col gap-4">
        <TaskTimePanel />
        <TaskComments />
      </div>
    </div>
  </Edit>
);

const TaskTimePanel = () => {
  const task = useRecordContext<TeamTask>();
  const translate = useTranslate();
  const formatDate = useFormatDate();
  const [hours, setHours] = useState("");
  const [description, setDescription] = useState("");
  const [create, { isPending }] = useCreate();
  const { data: entries, refetch } = useGetList(
    "time_entries",
    {
      filter: { task_id: task?.id },
      sort: { field: "date", order: "DESC" },
      pagination: { page: 1, perPage: 100 },
    },
    { enabled: !!task },
  );
  if (!task) return null;
  const total = (entries ?? []).reduce((sum, e) => sum + Number(e.hours), 0);

  const logTime = () => {
    const value = Number(hours.replace(",", "."));
    if (!value || value <= 0) return;
    create(
      "time_entries",
      {
        data: {
          task_id: task.id,
          deal_id: task.deal_id ?? null,
          date: todayIso(),
          hours: value,
          description: description || null,
        },
      },
      {
        onSuccess: () => {
          setHours("");
          setDescription("");
          refetch();
        },
      },
    );
  };

  return (
    <SectionCard
      title={translate("hub.team_tasks.time")}
      actions={
        <span className="text-sm text-muted-foreground">
          {translate("hub.team_tasks.hours_logged", { hours: total })}
          {task.estimate_hours ? ` / ${task.estimate_hours} h` : ""}
        </span>
      }
    >
      <div className="flex gap-2">
        <Input
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          placeholder="1.5"
          inputMode="decimal"
          className="w-20"
        />
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={translate("resources.time_entries.fields.description")}
        />
        <Button size="sm" onClick={logTime} disabled={isPending || !hours}>
          {translate("hub.common.add")}
        </Button>
      </div>
      <ul className="space-y-1 text-sm">
        {entries?.map((entry) => (
          <li key={entry.id} className="flex items-center gap-2">
            <SaleAvatar saleId={entry.sales_id} className="size-5" />
            <span className="tabular-nums">{entry.hours} h</span>
            <span className="truncate text-muted-foreground">
              {entry.description}
            </span>
            <span className="ml-auto text-xs text-muted-foreground">
              {formatDate(entry.date, { day: "numeric", month: "short" })}
            </span>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
};

const TaskComments = () => {
  const task = useRecordContext<TeamTask>();
  const translate = useTranslate();
  const formatDate = useFormatDate();
  const { identity } = useGetIdentity();
  const [text, setText] = useState("");
  const [create, { isPending }] = useCreate();
  const [deleteOne] = useDelete();
  const { data: comments, refetch } = useGetList(
    "team_task_comments",
    {
      filter: { task_id: task?.id },
      sort: { field: "created_at", order: "ASC" },
      pagination: { page: 1, perPage: 200 },
    },
    { enabled: !!task },
  );
  if (!task) return null;

  const send = () => {
    if (!text.trim()) return;
    create(
      "team_task_comments",
      { data: { task_id: task.id, text: text.trim() } },
      {
        onSuccess: () => {
          setText("");
          refetch();
        },
      },
    );
  };

  return (
    <SectionCard title={translate("hub.team_tasks.comments")}>
      <ul className="space-y-3">
        {comments?.map((comment) => (
          <li key={comment.id} className="group flex gap-2 text-sm">
            <SaleAvatar saleId={comment.sales_id} />
            <div className="min-w-0 flex-1">
              <p className="whitespace-pre-wrap">{comment.text}</p>
              <p className="text-xs text-muted-foreground">
                {formatDate(comment.created_at, {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
            {comment.sales_id === identity?.id && (
              <button
                type="button"
                className="opacity-0 group-hover:opacity-100"
                aria-label={translate("hub.common.delete")}
                onClick={() =>
                  deleteOne(
                    "team_task_comments",
                    { id: comment.id, previousData: comment },
                    { onSuccess: () => refetch() },
                  )
                }
              >
                <Trash2 className="size-3.5 text-muted-foreground" />
              </button>
            )}
          </li>
        ))}
      </ul>
      <div className="flex items-end gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={translate("hub.team_tasks.add_comment")}
          rows={2}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
          }}
        />
        <Button
          size="icon"
          onClick={send}
          disabled={isPending || !text.trim()}
          aria-label={translate("hub.common.add")}
        >
          <Send className="size-4" />
        </Button>
      </div>
    </SectionCard>
  );
};
